import endpoints from "@/utils/constants";
import { apiHelper } from "@/utils/helpers";
import productUtil from "@/utils/ProductUtil";
import { camelToSnake } from "@/utils/CamelToSnake";
import snakeToCamel from "@/utils/SnakeToCamel";

const actions = {
  /* ----------- Products apis ------------- */

  // storing product
  saveProduct(context) {
    return new Promise((resolve, reject) => {
      apiHelper
        .call(
          "post",
          `${endpoints.BASE_URL}${endpoints.API.products}`,
          context.state.product
        )
        .then(res => {
          if (res.status === 201) {
            resolve(res.data);
          }
        })
        .catch(err => {
          reject(err);
        });
    });
  },

  // getting all products
  getProducts(context, query) {
    const url = new URL(`${endpoints.BASE_URL}${endpoints.API.products}`);
    if (query) {
      url.search = new URLSearchParams(query);
    }
    return new Promise((resolve, reject) => {
      apiHelper.call("get", url).then(res => {
        context.commit("setProducts", res.data);
        resolve(res.data);
      });
    });
  },

  // getting data of single product
  getProduct(context, id) {
    return new Promise((resolve, reject) => {
      apiHelper
        .call("get", `${endpoints.BASE_URL}${endpoints.API.products}/${id}`)
        .then(async res => {
          if (res.status === 200) {
            const product = await productUtil.transform(res.data);
            context.commit("setProductData", product);
          }
          resolve(res.data);
        });
    });
  },

  updateProduct(context) {
    return new Promise((resolve, reject) => {
      apiHelper
        .call(
          "put",
          `${endpoints.BASE_URL}${endpoints.API.products}/${context.state.product.productId}`,
          context.state.product
        )
        .then(res => {
          if (res.status === 200) {
            context.commit("setProductData", res.data);
            resolve(res);
          }
        })
        .catch(err => {
          reject(err);
        });
    });
  },

  fetchProductForEdit(_, productId) {
    return new Promise((resolve, reject) => {
      apiHelper
        .call("get", `${endpoints.BASE_URL}${endpoints.API.products}/${productId}`)
        .then(res => {
          if (res.status === 200) {
            resolve(productUtil.transform(res.data));
          }
        })
        .catch(err => {
          reject(err);
        });
    });
  },

  updateProductById(_, product) {
    return new Promise((resolve, reject) => {
      apiHelper
        .call(
          "put",
          `${endpoints.BASE_URL}${endpoints.API.products}/${product.productId}`,
          product
        )
        .then(res => {
          if (res.status === 200) {
            resolve(res.data);
          }
        })
        .catch(err => {
          reject(err);
        });
    });
  },

  bulkFetchProductsForEdit(_, productIds) {
    return new Promise((resolve, reject) => {
      apiHelper
        .call(
          "post",
          `${endpoints.LARAVEL_BASE_URL}/api/v1/products/bulk/fetch`,
          { product_ids: productIds }
        )
        .then(res => {
          if (res.status !== 200) {
            reject(new Error("Unexpected response while loading products."));
            return;
          }

          const rawProducts = res.data && res.data.products ? res.data.products : [];
          const products = rawProducts.map(product => {
            try {
              return productUtil.transform(snakeToCamel(product));
            } catch (error) {
              console.error("Failed to transform product for edit:", product, error);
              throw error;
            }
          });
          resolve(products);
        })
        .catch(err => {
          reject(err);
        });
    });
  },

  bulkUpdateProducts(_, products) {
    return new Promise((resolve, reject) => {
      apiHelper
        .call(
          "post",
          `${endpoints.LARAVEL_BASE_URL}/api/v1/products/bulk/update`,
          {
            products: products.map(product => camelToSnake(product))
          }
        )
        .then(res => {
          if (res.status === 200) {
            resolve(res.data);
          }
        })
        .catch(err => {
          reject(err);
        });
    });
  },

  getProductForPos(_, query) {
    const url = new URL(
      `${endpoints.BASE_URL}${endpoints.API.products}/${query.productId}/for/pos`
    );
    if (query) {
      url.search = new URLSearchParams(query);
    }
    return new Promise((resolve, reject) => {
      apiHelper
        .call("get", url)
        .then(res => {
          resolve(res.data.product);
        })
        .catch(err => {
          reject(err);
        });
    });
  },

  /* ----------- Product Units apis ------------- */
  //create product unit
  saveProductUnit(context) {
    return new Promise((resolve, reject) => {
      apiHelper
        .call(
          "post",
          `${endpoints.BASE_URL}${endpoints.API.productUnits}`,
          context.state.productUnit
        )
        .then(res => {
          if (res.status === 201) {
            resolve(res.data);
          }
        })
        .catch(err => {
          reject(err);
        });
    });
  },

  // getting all product units
  getProductUnits(context) {
    return new Promise((resolve, reject) => {
      apiHelper
        .call("get", `${endpoints.BASE_URL}${endpoints.API.productUnits}`)
        .then(res => {
          if (res.status === 200) {
            context.commit("setProductUnits", res.data);
          }
        });
    });
  },

  fetchProductUnit(context, productUnitId) {
    return new Promise((resolve, reject) => {
      apiHelper
        .call(
          "get",
          `${endpoints.BASE_URL}${endpoints.API.productUnits}/${productUnitId}`
        )
        .then(res => {
          if (res.status === 200) {
            resolve(res.data);
          }
        })
        .catch(err => {
          reject(err);
        });
    });
  },
  updateProductUnit(context) {
    return new Promise((resolve, reject) => {
      apiHelper
        .call(
          "put",
          `${endpoints.BASE_URL}${endpoints.API.productUnits}/${context.state.productUnit.productUnitId}`,
          context.state.productUnit
        )
        .then(res => {
          if (res.status === 200) {
            context.commit("setProductUnit", res.data);
            resolve(res);
          }
        })
        .catch(err => {
          reject(err);
        });
    });
  },

  /* ----------- Products apis ------------- */

  // getting all product price types
  getProductPriceTypes(context) {
    return new Promise((resolve, reject) => {
      apiHelper
        .call("get", `${endpoints.BASE_URL}${endpoints.API.productPriceTypes}`)
        .then(res => {
          if (res.status === 200) {
            // const defaultProductPriceType = res.data.find(i => i.label === 'Retail')
            context.commit("setProductPriceTypes", res.data);
            // context.commit('setDefaultProductPriceType', defaultProductPriceType.productPriceTypeId);
          }
        });
    });
  },

  getBarcode(context) {
    return apiHelper.call(
      "get",
      `${endpoints.BASE_URL}${endpoints.API.products}/generate/barcode`
    );
  },

  exportProductList(context, query) {
    const url = new URL(`${endpoints.LARAVEL_BASE_URL}/api/v1/export/products`);
    if (query) {
      url.search = new URLSearchParams(query);
    }
    return new Promise((resolve, reject) => {
      apiHelper
        .call("get", url, {
          responseType: "blob"
        })
        .then(res => {
          if (res.status === 200) {
            resolve(res);
          }
        })
        .catch(err => {
          reject(err);
        });
    });
  },

  importProductFile(context, { file, branchId, importId }) {
    const formData = new FormData();
    formData.append("file", file);
    if (branchId)  formData.append("branch_id",  branchId);
    if (importId)  formData.append("import_id",  importId);
    return new Promise((resolve, reject) => {
      apiHelper
        .call(
          "post",
          `${endpoints.LARAVEL_BASE_URL}/api/v1/import/products`,
          formData,
          {
            headers: {
              "Content-Type": "multipart/form-data"
            }
          }
        )
        .then(res => {
          resolve(res);
        })
        .catch(err => {
          reject(err);
        });
    });
  }
};

export default actions;
